import { useEffect, useState } from "react";
import api, { formatApiError, money } from "../lib/api";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Loader2, Sparkles, Gift, CreditCard } from "lucide-react";
import { toast } from "sonner";

export default function PayDialog({ open, onOpenChange, bill }) {
  const [preview, setPreview] = useState(null);
  const [redirecting, setRedirecting] = useState(false);

  useEffect(() => {
    if (open && bill) {
      setPreview(null);
      api.get(`/bills/${bill.id}/pay-preview`).then((r) => setPreview(r.data)).catch(() => {});
    }
  }, [open, bill]);

  const pay = async () => {
    setRedirecting(true);
    try {
      const { data } = await api.post("/payments/bill-checkout", { bill_id: bill.id, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      setRedirecting(false);
    }
  };

  if (!bill) return null;
  const sym = preview?.currency_symbol || "$";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="pay-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Pay & Save</DialogTitle>
          <DialogDescription>{bill.provider_name} • {bill.category_name}</DialogDescription>
        </DialogHeader>

        {preview ? (
          <div>
            <div className="bg-slate-50 rounded-xl border border-slate-200 divide-y divide-slate-200">
              <Row label="Original bill" value={money(preview.original, sym)} />
              <Row label="Discount" value={`- ${money(preview.discount, sym)}`} accent="text-emerald-600" />
              <div className="flex justify-between px-4 py-3 bg-slate-900 text-white rounded-b-xl">
                <span className="font-display font-bold">Amount to pay</span>
                <span className="font-display font-extrabold text-emerald-400" data-testid="pay-amount">{money(preview.amount_to_pay, sym)}</span>
              </div>
            </div>
            <div className="flex gap-3 mt-3">
              <div className="flex-1 bg-amber-50 rounded-xl p-3 flex items-center gap-2">
                <Gift className="w-5 h-5 text-amber-600" />
                <div><p className="text-xs text-slate-500 font-bold uppercase">Rewards</p><p className="font-display font-bold text-slate-900">{preview.reward_points} pts</p></div>
              </div>
              {preview.cashback > 0 && (
                <div className="flex-1 bg-emerald-50 rounded-xl p-3 flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-emerald-600" />
                  <div><p className="text-xs text-slate-500 font-bold uppercase">Cashback</p><p className="font-display font-bold text-emerald-700">{money(preview.cashback, sym)}</p></div>
                </div>
              )}
            </div>
            <Button onClick={pay} disabled={redirecting} data-testid="pay-now-btn" className="w-full mt-4 rounded-full bg-emerald-500 hover:bg-emerald-600 font-bold py-6">
              {redirecting ? <Loader2 className="w-5 h-5 animate-spin" /> : <><CreditCard className="w-5 h-5 mr-2" /> PAY NOW</>}
            </Button>
            <p className="text-xs text-slate-400 text-center mt-2">Secure payment via Stripe · test card 4242 4242 4242 4242</p>
          </div>
        ) : (
          <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-emerald-500" /></div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Row({ label, value, accent }) {
  return (
    <div className="flex justify-between px-4 py-3">
      <span className="text-sm font-semibold text-slate-500">{label}</span>
      <span className={`text-sm font-bold ${accent || "text-slate-900"}`}>{value}</span>
    </div>
  );
}
